import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyAddressUrl,
  usePropertyField,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
  useEditMode,
} from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';

// ============================================================================
// Local helpers
// ============================================================================

/**
 * Remove a trailing country segment (USA / U.S.A. / United States [of America])
 * plus any leading comma/whitespace. Keeps the visible address focused on the
 * street/city/state on the marketing page.
 */
function stripCountry(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .replace(/\s*,?\s*(u\.?\s*s\.?\s*a\.?|united\s+states(\s+of\s+america)?)\s*$/i, '')
    .trim();
}

interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface CondensedHourRow {
  dayLabel: string;
  timeLabel: string;
}

function formatTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const [h, m] = trimmed.split(':').map(Number);
  if (isNaN(h)) return trimmed;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${suffix}` : `${hr} ${suffix}`;
}

/**
 * Collapses consecutive days that share the same open/close times into a
 * single range row, e.g. Mon–Fri: 9 AM – 6 PM.
 */
function condenseOfficeHours(hours: OfficeHourEntry[]): CondensedHourRow[] {
  const rows: CondensedHourRow[] = [];
  let i = 0;
  while (i < hours.length) {
    const cur = hours[i];
    const timeKey = `${cur.open_time ?? ''}|${cur.close_time ?? ''}`;
    let j = i + 1;
    while (j < hours.length) {
      const nxt = hours[j];
      if (`${nxt.open_time ?? ''}|${nxt.close_time ?? ''}` !== timeKey) break;
      j++;
    }
    const span = j - i;
    const dayLabel =
      span === 1
        ? (cur.day ?? '')
        : `${cur.day ?? ''} – ${hours[j - 1].day ?? ''}`;
    const timeLabel =
      cur.open_time && cur.close_time
        ? `${formatTime(cur.open_time)} – ${formatTime(cur.close_time)}`
        : 'Closed';
    rows.push({ dayLabel, timeLabel });
    i = j;
  }
  return rows;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

function normalizeDayKey(day: string | null | undefined): string {
  return (day ?? '').trim().toLowerCase().slice(0, 3);
}

/**
 * Given the raw office_hours collection, return an entry for every day of the
 * week — filling any missing / partially-blank days with a "closed" placeholder
 * so `condenseOfficeHours` renders them as "Closed" rows.
 */
function padWithClosedDays(hours: OfficeHourEntry[]): OfficeHourEntry[] {
  const byKey = new Map<string, OfficeHourEntry>();
  for (const entry of hours) {
    const key = normalizeDayKey(entry.day);
    if (key && DAY_ORDER.includes(key as (typeof DAY_ORDER)[number]) && !byKey.has(key)) {
      byKey.set(key, entry);
    }
  }
  return DAY_ORDER.map((key) => {
    const existing = byKey.get(key);
    return {
      day: DAY_LABELS[key],
      open_time: existing?.open_time ?? null,
      close_time: existing?.close_time ?? null,
    };
  });
}

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_place_url',
      suggestedFields: ['property_google_place_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const tiktokLinkDef: SlotGroupDefinition = {
  label: 'TikTok',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_tiktok_url',
      suggestedFields: ['property_tiktok_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'TikTok' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  useSiteCtaStyles();
  const title = useContentToken('contactform.title');
  const addressLabel = useContentToken('contactform.address_label');
  const hoursLabel = useContentToken('contactform.hours_label');

  const addressLink = useSlotGroup('contactform.address', addressLinkDef);
  const facebook = useSlotGroup('contactform.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('contactform.instagram_link', instagramLinkDef);
  const tiktok = useSlotGroup('contactform.tiktok_link', tiktokLinkDef);
  const isEditMode = Boolean(useEditMode()?.isEditMode);

  const propertyAddressUrl = usePropertyAddressUrl();
  const propertyAddress = usePropertyField('property_full_address') as
    | string
    | null
    | undefined;

  const officeHours = useCollectionBinding<OfficeHourEntry>(
    'contactform.office_hours',
    { collection: 'office_hours' },
  );

  const formSlot = useBlockSlot('contactform.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  const addressText = stripCountry(addressLink.text || propertyAddress || '');
  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const hoursRows =
    officeHours.length > 0 ? condenseOfficeHours(padWithClosedDays(officeHours)) : [];
  const hasSocial = Boolean(facebook.url || instagram.url || tiktok.url);

  return (
    <section className="cf-section">
      <div className="cf-container">
        <div className="cf-grid">
          {/* Left column: Address, Hours, Socials */}
          <aside className="cf-info-col">
            <EditableToken
              path="contactform.address_label"
              as="h3"
              className="cf-info-heading"
            >
              {addressLabel || 'Address'}
            </EditableToken>
            <EditableSlotGroup
              groupPath="contactform.address"
              definition={addressLinkDef}
            >
              {addressLink.url || propertyAddressUrl ? (
                <a
                  href={addressHref}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="cf-info-text cf-info-link"
                >
                  {addressText}
                </a>
              ) : (
                <span className="cf-info-text">{addressText}</span>
              )}
            </EditableSlotGroup>

            {hoursRows.length > 0 && (
              <>
                <EditableToken
                  path="contactform.hours_label"
                  as="h3"
                  className="cf-info-heading cf-info-heading--spaced"
                >
                  {hoursLabel || 'Hours'}
                </EditableToken>
                <div className="cf-hours">
                  {hoursRows.map((row, i) => (
                    <div key={i} className="cf-hours-row">
                      <span className="cf-hours-day">{row.dayLabel}</span>
                      <span className="cf-hours-time">{row.timeLabel}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className={`cf-socials${hasSocial ? '' : ' cf-socials--empty'}`}>
              <EditableSlotGroup
                groupPath="contactform.facebook_link"
                definition={facebookLinkDef}
              >
                {facebook.url ? (
                  <a
                    href={facebook.url}
                    target={facebook._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={facebook.label || 'Facebook'}
                    className="cf-social-link"
                  >
                    <FontAwesomeIcon icon={faFacebookF} />
                  </a>
                ) : (
                  <span className="cf-social-link cf-social-link--placeholder" aria-hidden="true">
                    <FontAwesomeIcon icon={faFacebookF} />
                  </span>
                )}
              </EditableSlotGroup>
              <EditableSlotGroup
                groupPath="contactform.instagram_link"
                definition={instagramLinkDef}
              >
                {instagram.url ? (
                  <a
                    href={instagram.url}
                    target={instagram._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={instagram.label || 'Instagram'}
                    className="cf-social-link"
                  >
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                ) : (
                  <span className="cf-social-link cf-social-link--placeholder" aria-hidden="true">
                    <FontAwesomeIcon icon={faInstagram} />
                  </span>
                )}
              </EditableSlotGroup>
              {tiktok.url || isEditMode ? (
                <EditableSlotGroup
                  groupPath="contactform.tiktok_link"
                  definition={tiktokLinkDef}
                >
                  {tiktok.url ? (
                    <a
                      href={tiktok.url}
                      target={tiktok._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={tiktok.label || 'TikTok'}
                      className="cf-social-link"
                    >
                      <FontAwesomeIcon icon={faTiktok} />
                    </a>
                  ) : (
                    <span className="cf-social-link cf-social-link--placeholder" aria-hidden="true">
                      <FontAwesomeIcon icon={faTiktok} />
                    </span>
                  )}
                </EditableSlotGroup>
              ) : null}
            </div>
          </aside>

          {/* Right column: title + form */}
          <div className="cf-form-col">
            <EditableToken
              path="contactform.title"
              as="h2"
              className="cf-title"
            >
              {title || 'Contact Us'}
            </EditableToken>

            <EditableToken
              path="contactform.form_block"
              as="div"
              className="cf-form-wrap"
            >
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <p className="cf-form-empty">No form block selected</p>
              )}
            </EditableToken>
          </div>
        </div>
      </div>

      <style>{`
        .cf-section {
          background: white;
          padding: 3rem 1.5rem;
        }
        @media (min-width: 768px) {
          .cf-section { padding: 5rem 2rem; }
        }
        .cf-container {
          max-width: 76rem;
          margin: 0 auto;
        }
        .cf-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 900px) {
          .cf-grid {
            grid-template-columns: minmax(220px, 1fr) minmax(0, 2fr);
            gap: 4rem;
            align-items: start;
          }
        }

        /* ── Info column ── */
        .cf-info-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        @media (min-width: 900px) {
          .cf-info-col {
            align-items: flex-start;
            text-align: left;
          }
        }
        .cf-info-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--vibe-color-primary);
          line-height: 1.2;
          margin: 0 0 1rem;
        }
        .cf-info-heading--spaced {
          margin-top: 2.25rem;
        }
        /* Match HeroSolidBG blurb: font-body text-base md:text-lg leading-relaxed */
        .cf-info-text {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.625;
          color: var(--vibe-color-text);
          text-decoration: none;
        }
        @media (min-width: 768px) {
          .cf-info-text { font-size: 1.125rem; }
        }
        .cf-info-link {
          transition: color 0.2s ease;
        }
        .cf-info-link:hover,
        .cf-info-link:focus-visible {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        /* ── Hours list ── */
        .cf-hours {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          align-items: center;
        }
        @media (min-width: 900px) {
          .cf-hours { align-items: stretch; }
        }
        .cf-hours-row {
          display: flex;
          align-items: baseline;
          gap: 1.5rem;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.625;
          color: var(--vibe-color-text);
        }
        @media (min-width: 768px) {
          .cf-hours-row { font-size: 1.125rem; }
        }
        .cf-hours-day {
          min-width: 6rem;
        }
        @media (max-width: 899.98px) {
          .cf-hours-day {
            min-width: 0;
            text-align: right;
          }
          .cf-hours-time {
            text-align: left;
          }
        }
        .cf-hours-time {
          color: var(--vibe-color-text);
        }

        /* ── Social row ── */
        .cf-socials {
          margin-top: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
        }
        @media (min-width: 900px) {
          .cf-socials { justify-content: flex-start; }
        }
        .cf-socials--empty {
          /* keep the row present (both wrappers must always render for the editor)
             but out of the way when neither URL is set */
          margin-top: 1rem;
          opacity: 0.35;
        }
        .cf-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--vibe-color-primary);
          font-size: 1.5rem;
          line-height: 1;
          transition: opacity 0.2s ease, transform 0.2s ease;
          text-decoration: none;
        }
        .cf-social-link:hover,
        .cf-social-link:focus-visible {
          opacity: 0.75;
          transform: translateY(-1px);
        }
        .cf-social-link--placeholder {
          cursor: default;
        }

        /* ── Form column ── */
        .cf-form-col {
          text-align: center;
        }
        @media (min-width: 900px) {
          .cf-form-col { text-align: left; }
        }
        .cf-title {
          font-family: var(--vibe-font-heading);
          font-size: 2.75rem;
          font-weight: 400;
          color: var(--vibe-color-primary);
          line-height: 1.1;
          letter-spacing: -0.01em;
          margin: 0 0 2rem;
        }
        @media (min-width: 768px) {
          .cf-title { font-size: 3.5rem; }
        }
        .cf-form-wrap {
          width: 100%;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }

        /* ──────────────────────────────────────────────────────────────
           Lead-form card chrome — the multihub-lead-form block is read-only
           and ships its own title + inline-styled submit button. Hide the
           block's form title (the page already has a Contact Us heading)
           and restyle the CTA to match Aion's primary → orange (surface)
           hover used on FloorplansList / Footer CTAs.
           ────────────────────────────────────────────────────────────── */
        .cf-form-wrap .mh-lead-form-card > h3 {
          display: none !important;
        }
        /* Spacer the block inserts when title is present but subtitle empty */
        .cf-form-wrap .mh-lead-form-card > h3 + div {
          display: none !important;
        }
        .cf-form-wrap .mh-lead-form-submit-button {
          padding: 1rem 2rem !important;
          min-width: 11rem;
          border-radius: 999px !important;
          border: 2px solid var(--vibe-color-primary) !important;
          background-color: var(--vibe-color-primary) !important;
          color: var(--vibe-color-text-on-primary, #ffffff) !important;
          font-family: var(--vibe-font-body, inherit) !important;
          font-size: 0.78rem !important;
          font-weight: 700 !important;
          letter-spacing: 0.16em;
          line-height: 1;
          text-transform: uppercase;
          transition:
            background-color 0.25s ease,
            color 0.25s ease,
            border-color 0.25s ease,
            transform 0.2s ease,
            box-shadow 0.25s ease !important;
        }
        .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled) {
          background-color: var(--vibe-color-surface, #e2903d) !important;
          border-color: var(--vibe-color-surface, #e2903d) !important;
          color: var(--site-cta-ink-on-surface, #ffffff) !important;
          transform: translateY(-2px);
        }
        .cf-form-wrap .mh-lead-form-submit-button:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 3px;
        }
        @media (prefers-reduced-motion: reduce) {
          .cf-form-wrap .mh-lead-form-submit-button,
          .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled) {
            transform: none;
            transition:
              background-color 0.25s ease,
              color 0.25s ease,
              border-color 0.25s ease !important;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    'contactform.address_label': {
      label: 'Address Heading',
      type: 'text',
      defaultValue: 'Address',
    },
    'contactform.hours_label': {
      label: 'Hours Heading',
      type: 'text',
      defaultValue: 'Hours',
    },
  },
  slotGroups: {
    'contactform.address': addressLinkDef,
    'contactform.facebook_link': facebookLinkDef,
    'contactform.instagram_link': instagramLinkDef,
    'contactform.tiktok_link': tiktokLinkDef,
  },
  collectionBindings: {
    'contactform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
